import React, {useEffect, useMemo, useState} from "react"
import {FloatButton} from "antd"
import {Main} from "./components/Main"
import {Redirect, Route, Switch} from "react-router-dom"
import "./assets/styles/main.css"
import "./assets/styles/responsive.css"
import "./assets/styles/styles.scss"
import {Objects} from "./pages/Object/Objects"
import NProgress from "nprogress"
import "nprogress/nprogress.css"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "./store/store"
import {PSLeakDeviceLogs} from "./pages/PSLeakDeviceLog/PSLeakDeviceLogs"
import {ObjectGroups} from "./pages/ObjectGroup/ObjectGroups"
import {PSLeakDevices} from "./pages/PSLeakDevice/PSLeakDevices"
import {BadRequest} from "./components/BadRequest"
import {NotifierGenerator} from "./components/NotifierGenerator"
import {ObjectGroup} from "./pages/ObjectGroup/ObjectGroup"
import {ObjectGroupForm} from "./pages/ObjectGroup/ObjectGroupForm"
import {Object} from "./pages/Object/Object"
import {ObjectForm} from "./pages/Object/ObjectForm"
import {PSLeakDevice} from "./pages/PSLeakDevice/PSLeakDevice"
import {PSLeakDeviceForm} from "./pages/PSLeakDevice/PSLeakDeviceForm"
import {PSLeakDeviceLog} from "./pages/PSLeakDeviceLog/PSLeakDeviceLog"
import {Login} from "./pages/Login/Login"
import {Loading} from "./components/Loading/Loading"
import {checkAuth} from "./store/appReducer"
import {catchError} from "./store/sliceHelper"
import {UserGroups} from "./pages/UserGroup/UserGroups"
import {UserGroupForm} from "./pages/UserGroup/UserGroupForm"
import {UserGroup} from "./pages/UserGroup/UserGroup"
import {Users} from "./pages/User/Users"
import {User} from "./pages/User/User"
import {UserForm} from "./pages/User/UserForm"
import Dashboard from "./pages/Dashboard/Dashboard"
import {ID_SYSTEM_COMPANY} from "./constants/constants"
import {Companies} from "./pages/Company/Companies"
import {Company} from "./pages/Company/Company"
import {CompanyForm} from "./pages/Company/CompanyForm"
import {PSLeakDeviceUpdate} from "./pages/PSLeakDevice/PSLeakDeviceUpdate"
import {PSLeakDeviceLocalLogs} from "./pages/PSLeakDeviceLocalLog/PSLeakDeviceLocalLogs"
import {PSLeakDeviceLocalLog} from "./pages/PSLeakDeviceLocalLog/PSLeakDeviceLocalLog"
import {PSLeakDeviceIndications} from "./pages/PSLeakDeviceIndication/PSLeakDeviceIndications"
import {PSLeakDeviceIndication} from "./pages/PSLeakDeviceIndication/PSLeakDeviceIndication"
import {AttributesVersions} from "./pages/AttributesVersion/AttributesVersions"
import {AttributesVersion} from "./pages/AttributesVersion/AttributesVersion"
import {AttributesVersionForm} from "./pages/AttributesVersion/AttributesVersionForm"
import {SecurityLogs} from "./pages/SecurityLog/SecurityLogs"
import {Settings} from "./pages/Settings"
import {UnregisterPSLeakDevices} from "./pages/UnregisterPSLeakDevice/UnregisterPSLeakDevices"
import {UserTypeEnum} from "./types/types"
import {UinList} from "./pages/UinList/UinList"
import {UinListForm} from "./pages/UinList/UinListForm"

export const App = () => {
    const dispatch = useDispatch()
    const idUser = useSelector((state: StateType) => state.appReducer.idUser)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const [appRdy, setAppRdy] = useState(false)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        (async () => {
            try {
                await dispatch(checkAuth({controller}))
            } catch (e: any) {
                catchError(e, dispatch)
            } finally {
                setAppRdy(true)
            }
        })()
    }, [controller])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [controller])

    useEffect(() => {
        NProgress.configure({showSpinner: false})
        if (status === 'loading') {
            NProgress.start()
        } else {
            NProgress.done()
        }
        return () => {
            NProgress.done()
        }
    }, [status])

    const routes = useMemo(() => {
        const arr: Array<any> = [
            <Route exact path="/" key="RedirectToDashboardFromMain">
                <Redirect to="/dashboard"/>
            </Route>,
            <Route exact path="/login" key="RedirectToDashboardFromLogin">
                <Redirect to="/dashboard"/>
            </Route>,
            <Route exact path="/dashboard" component={Dashboard} key="/Dashboard"/>,
            <Route exact path="/objectGroup" component={ObjectGroups} key="/ObjectGroups"/>,
            <Route exact path="/objectGroup/:id" component={ObjectGroup} key="/ObjectGroup"/>,
            <Route exact path="/objectGroupForm" component={ObjectGroupForm} key="/ObjectGroupForm"/>,
            <Route exact path="/object" component={Objects} key="/Objects"/>,
            <Route exact path="/object/:id" component={Object} key="/Object"/>,
            <Route exact path="/objectForm" component={ObjectForm} key="/ObjectForm"/>,
            <Route exact path="/psLeakDevice" component={PSLeakDevices} key="/PSLeakDevices"/>,
            <Route exact path="/psLeakDevice/:id" component={PSLeakDevice} key="/PSLeakDevice"/>,
            <Route exact path="/psLeakDeviceForm" component={PSLeakDeviceForm} key="/PSLeakDeviceForm"/>,
            <Route exact path="/psLeakDeviceUpdate/:id" component={PSLeakDeviceUpdate} key="/PSLeakDeviceUpdate"/>,
            <Route exact path="/psLeakDeviceLog" component={PSLeakDeviceLogs} key="/PSLeakDeviceLogs"/>,
            <Route exact path="/psLeakDeviceLog/:id" component={PSLeakDeviceLog} key="/PSLeakDeviceLog"/>,
            <Route exact path="/psLeakDeviceLocalLog" component={PSLeakDeviceLocalLogs} key="/PSLeakDeviceLocalLogs"/>,
            <Route exact path="/psLeakDeviceLocalLog/:id" component={PSLeakDeviceLocalLog} key="/PSLeakDeviceLocalLog"/>,
            <Route exact path="/psLeakDeviceIndication" component={PSLeakDeviceIndications} key="/PSLeakDeviceIndications"/>,
            <Route exact path="/psLeakDeviceIndication/:id" component={PSLeakDeviceIndication} key="/PSLeakDeviceIndication"/>,
            <Route exact path="/securityLog" component={SecurityLogs} key="/SecurityLogs"/>,
        ]

        if (profile?.idCompany === ID_SYSTEM_COMPANY) {
            arr.push(
                <Route exact path="/company" component={Companies} key="/Companies"/>,
                <Route exact path="/company/:id" component={Company} key="/Company"/>,
                <Route exact path="/companyForm" component={CompanyForm} key="/CompanyForm"/>,
                <Route exact path="/unregisterPsLeakDevice" component={UnregisterPSLeakDevices} key="/unregisterPsLeakDevice"/>,
            )
        }

        if (profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM || profile?.idUserType === UserTypeEnum.OPERATOR_SYSTEM || profile?.idUserType === UserTypeEnum.BUH_SYSTEM) {
            arr.push(
                <Route exact path="/attributesVersion" component={AttributesVersions} key="/AttributesVersions"/>,
                <Route exact path="/attributesVersion/:id" component={AttributesVersion} key="/AttributesVersion"/>,
                <Route exact path="/attributesVersionForm" component={AttributesVersionForm} key="/AttributesVersionForm"/>,
            )
        }

        if (profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM || profile?.idUserType === UserTypeEnum.ADMIN_COMPANY) {
            arr.push(
                <Route exact path="/userGroup" component={UserGroups} key="/UserGroups"/>,
                <Route exact path="/userGroup/:id" component={UserGroup} key="/UserGroup"/>,
                <Route exact path="/userGroupForm" component={UserGroupForm} key="/UserGroupForm"/>,
                <Route exact path="/user" component={Users} key="/Users"/>,
                <Route exact path="/user/:id" component={User} key="/User"/>,
                <Route exact path="/userForm" component={UserForm} key="/UserForm"/>,
                <Route exact path="/psLeakUinList" component={UinList} key="/UinList"/>,
                <Route exact path="/psLeakUinListForm" component={UinListForm} key="/UinListForm"/>,
            )
        }

        arr.push(<Route path="/Settings" component={Settings} key="/Settings"/>)
        arr.push(<Route path="*" component={BadRequest} key="/BadRequest"/>)
        return arr
    }, [profile])

    if (!appRdy) return <div style={{height: '100vh'}}><Loading style={{padding: 0}}/></div>

    return (
        <>
            {
                idUser
                    ? <Main profile={profile}
                            dispatch={dispatch}
                            controller={controller}
                    >
                        <Switch>
                            {routes}
                        </Switch>
                    </Main>
                    : <Switch>
                        <Route exact path="/login" component={Login}/>
                        <Route path="*">
                            <Redirect to="/login"/>
                        </Route>
                    </Switch>
            }
            <NotifierGenerator />
            <FloatButton.BackTop style={{left: 30}} />
        </>
    )
}
